import { useEffect, useState } from 'react';
import { getAllConfigs, updateConfig, createConfig } from '../../../../api/administration';
import { toast } from 'react-toastify';
import SystemConfigsHeader from './components/SystemConfigsHeader';
import SystemConfigsTable from './components/SystemConfigsTable';
import ConfigValueModal from './components/ConfigValueModal';
import AddSystemConfigModal from './components/AddSystemConfigModal';

const SystemConfigs = () => {
  const [configs, setConfigs] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(15);

  // View/edit modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Add config modal state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const getData = async () => {
    setLoading(true);
    try {
      const response = await getAllConfigs(1, 100);
      console.log('Configs data:', response);

      if (response && response.data) {
        const data = response.data;
        // API returns: { message: "...", data: { totalRecords, pageNum, pageSize, records: [...] } }
        const configsList = data.data?.records || data.records || data.data || [];
        console.log('Configs list extracted:', configsList);
        setConfigs(Array.isArray(configsList) ? configsList : []);
      } else if (Array.isArray(response)) {
        setConfigs(response);
      }
    } catch (error) {
      console.error('Error fetching configs:', error);
      toast.error('Something went wrong while fetching configurations');
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
  };

  const handleViewConfig = (configId) => {
    const config = configs.find((c) => c.id === configId);
    if (config) {
      console.log('View config:', config);
      toast.info(`Config: ${config.client} - ${config.type}`);
    }
  };

  // Handle clicking on Type to open modal with config value display
  const handleTypeClick = (configId) => {
    const config = configs.find((c) => c.id === configId);
    if (config) {
      console.log('Opening modal for config:', config);
      setSelectedConfig(config);
      setIsModalOpen(true);
    }
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedConfig(null);
  };

  // Save changes from modal
  const handleSave = async (updatedValue) => {
    if (!selectedConfig) {
      toast.error('No configuration selected');
      return;
    }

    setIsSaving(true);
    try {
      console.log('Saving config:', selectedConfig.id, 'with value:', updatedValue);

      // Build the payload - update the value object
      const payload = {
        value: updatedValue,
      };

      await updateConfig(selectedConfig.id, payload);

      // Update local state
      setConfigs((prev) =>
        prev.map((c) =>
          c.id === selectedConfig.id ? { ...c, value: updatedValue } : c
        )
      );

      // Update selectedConfig to reflect changes
      setSelectedConfig((prev) => ({ ...prev, value: updatedValue }));

      toast.success('Configuration saved successfully');
      getData(); // Refresh data
    } catch (error) {
      console.error('Error saving config:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save configuration';
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  // Create new config via POST /config
  const handleAddConfigSave = async (payload) => {
    setIsCreating(true);
    try {
      await createConfig(payload);
      toast.success('Configuration created successfully');
      setIsAddModalOpen(false);
      getData();
    } catch (error) {
      console.error('Error creating config:', error);
      const status = error.response?.status;
      const errorMessage = error.response?.data?.message || error.message;
      if (status === 409) {
        toast.error('A configuration with this type already exists');
      } else {
        toast.error(errorMessage || 'Failed to create configuration');
      }
    } finally {
      setIsCreating(false);
    }
  };

  // Filter configs based on search term (client-side filtering)
  const filteredConfigs = configs.filter((config) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      config.client?.toLowerCase().includes(searchLower) ||
      config.company?.toLowerCase().includes(searchLower) ||
      config.division?.toLowerCase().includes(searchLower) ||
      config.type?.toLowerCase().includes(searchLower) ||
      config.created_by?.toLowerCase().includes(searchLower) ||
      config.updated_by?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="p-5">
      <div className="border border-gray-300 rounded-lg relative">
        <SystemConfigsHeader
          onReload={getData}
          isLoading={isLoading}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          onAddConfig={() => setIsAddModalOpen(true)}
        />

        <div className="relative">
          <SystemConfigsTable
            data={filteredConfigs}
            onViewConfig={handleViewConfig}
            onTypeClick={handleTypeClick}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
          />
          {isLoading && (
            <div
              className="absolute left-0 right-0 flex flex-col items-center justify-center z-20"
              style={{ top: '64px', bottom: 0, background: 'rgba(255,255,255,0.9)' }}
            >
              <div className="w-10 h-10 border-4 border-secondary-200 border-t-secondary-700 rounded-full animate-spin mb-3"></div>
              <span className="text-lg text-secondary-700 font-semibold flex items-center">
                Loading
                <span className="inline-flex ml-1">
                  <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Config Value Modal - displays value like Settings page */}
      <ConfigValueModal
        isOpen={isModalOpen}
        onClose={closeModal}
        config={selectedConfig}
        onSave={handleSave}
        isSubmitting={isSaving}
      />

      {/* Add Configuration Modal */}
      <AddSystemConfigModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleAddConfigSave}
        isSubmitting={isCreating}
      />
    </div>
  );
};

export default SystemConfigs;
